import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function DataTable({
  columns = [],
  data,
  rowKey = "id",
  loading = false,
  loadingMessage = "Loading...",
  emptyMessage = "No data found.",
  // Custom ReactNode rendered inside a <td> when data is empty
  emptyState,
  // Replaces the default base class applied to every <tr>
  rowClassName,
  // Extra className applied to every <td> before column-level cellClassName
  defaultCellClassName,
  getRowClassName,
  // (row, index) => object of extra props spread onto <tr>
  getRowProps,
  // (row, index) => void for custom row-click behavior
  onRowClick,
  // when true, clicking row opens built-in preview popup (if onRowClick is not provided)
  enableRowPreview = true,
  children,
  wrapperClassName,
  tableClassName,
  headClassName,
  headerCellClassName,
  bodyClassName,
}) {
  const hasManagedRows = Array.isArray(data);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);

  const isIdLikeKey = (key) => {
    const raw = String(key || "").trim();
    const lower = raw.toLowerCase();
    return lower === "id" || lower.endsWith("_id") || /(?:[a-z0-9])id$/.test(raw);
  };

  const stripIdFields = (value) => {
    if (Array.isArray(value)) return value.map(stripIdFields);
    if (value && typeof value === "object") {
      if (value instanceof Date) return value;
      return Object.entries(value).reduce((acc, [key, val]) => {
        if (isIdLikeKey(key)) return acc;
        acc[key] = stripIdFields(val);
        return acc;
      }, {});
    }
    return value;
  };

  const previewEntries = useMemo(() => {
    if (!previewRow || typeof previewRow !== "object") return [];
    return Object.entries(previewRow).filter(([key]) => !isIdLikeKey(key));
  }, [previewRow]);

  const formatValue = (value) => {
    const sanitized = stripIdFields(value);

    if (sanitized === null || sanitized === undefined || sanitized === "") return "—";
    if (typeof sanitized === "string" || typeof sanitized === "number" || typeof sanitized === "boolean") {
      return String(sanitized);
    }
    if (sanitized instanceof Date) return sanitized.toISOString();
    try {
      return JSON.stringify(sanitized, null, 2);
    } catch {
      return String(sanitized);
    }
  };

  const handleRowClick = (event, row, index) => {
    const interactive = event.target.closest("button, a, input, select, textarea, [role='button'], [data-row-click-ignore='true']");
    if (interactive) return;

    if (typeof onRowClick === "function") {
      onRowClick(row, index);
      return;
    }

    if (enableRowPreview) {
      setPreviewRow(row);
      setPreviewOpen(true);
    }
  };

  const resolveRowKey = (row, index) => {
    if (typeof rowKey === "function") return rowKey(row, index);
    return row?.[rowKey] ?? index;
  };

  const renderManagedBody = () => {
    if (loading) {
      return (
        <tr className="border-t border-slate-100 text-sm">
          <td className="px-5 py-4 text-slate-500" colSpan={columns.length || 1}>
            {loadingMessage}
          </td>
        </tr>
      );
    }

    if (!data.length) {
      if (emptyState) {
        return (
          <tr className="border-t border-slate-100 text-sm">
            <td colSpan={columns.length || 1}>{emptyState}</td>
          </tr>
        );
      }
      return (
        <tr className="border-t border-slate-100 text-sm">
          <td className="px-5 py-4 text-slate-500" colSpan={columns.length || 1}>
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return data.map((row, index) => {
      const extraRowProps = typeof getRowProps === "function" ? getRowProps(row, index) : {};
      return (
        <tr
          key={resolveRowKey(row, index)}
          className={cn(
            rowClassName ?? "border-t border-slate-100 text-sm hover:bg-slate-50/50",
            (enableRowPreview || typeof onRowClick === "function") && "cursor-pointer",
            typeof getRowClassName === "function" ? getRowClassName(row, index) : getRowClassName,
          )}
          onClick={(event) => handleRowClick(event, row, index)}
          {...extraRowProps}
        >
          {columns.map((column) => {
            const content = typeof column.render === "function" ? column.render(row, index) : row?.[column.key];
            const cellClassName =
              typeof column.cellClassName === "function" ? column.cellClassName(row, index) : column.cellClassName;

            return (
              <td key={column.key} className={cn("px-5 py-3.5 align-middle", defaultCellClassName, cellClassName)}>
                {content ?? "—"}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <>
      <div className={cn("overflow-auto w-full", wrapperClassName)}>
        <table className={cn("w-full min-w-max", tableClassName)}>
          <thead
            className={cn(
              "text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500",
              headClassName,
            )}
          >
            <tr>
              {columns.map((column) => (
                  <th
                    key={column.key}
                    className={cn(
                      "px-5 py-3 whitespace-nowrap sticky top-0 z-30 bg-slate-50 border-b border-slate-200",
                      headerCellClassName,
                      column.className,
                    )}
                  >
                    {column.label}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody className={bodyClassName}>{hasManagedRows ? renderManagedBody() : children}</tbody>
        </table>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>View Details</DialogTitle>
            <DialogDescription>Row data preview</DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-slate-200">
            {previewEntries.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-500">No data available.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {previewEntries.map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-3 px-4 py-3 text-sm">
                    <p className="col-span-1 font-semibold text-slate-600 break-words">{key}</p>
                    <pre className="col-span-2 whitespace-pre-wrap break-words text-slate-900 font-mono text-xs leading-5">
                      {formatValue(value)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
